import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Chip,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { fetchOrders } from '../ordersApi';
import { useBranch } from '../BranchContext';

export default function OrderHistory() {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const { selectedBranch } = useBranch();

  useEffect(() => {
    loadOrders();
  }, [selectedBranch]);

  const loadOrders = async () => {
    try {
      const branchId = selectedBranch ? (selectedBranch.id || selectedBranch.Id) : null;
      const data = await fetchOrders(branchId);
      // Filter for Served and Delivered orders (and Completed as legacy/fallback)
      const completedOrders = data.filter(order => {
        const status = (order.orderStatus || order.OrderStatus || order.status || order.Status || '').toLowerCase();
        return status === 'served' || status === 'delivered' || status === 'completed';
      });
      // Sort by date descending
      completedOrders.sort((a, b) => {
          const dateA = new Date(a.orderDate || a.OrderDate || a.createdDate || a.CreatedDate);
          const dateB = new Date(b.orderDate || b.OrderDate || b.createdDate || b.CreatedDate);
          return dateB - dateA;
      });
      setOrders(completedOrders);
    } catch (error) {
      console.error('Error loading order history:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    const searchLower = searchTerm.toLowerCase();
    const orderId = (order.orderId || order.OrderId || '').toString();
    const customerName = (order.customerName || order.CustomerName || '').toLowerCase();
    return orderId.includes(searchLower) || customerName.includes(searchLower);
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'Served':
      case 'Delivered':
      case 'Completed':
        return 'success';
      case 'Cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleOpenOrder = (order) => {
    setSelectedOrder(order);
    setDetailsOpen(true);
  };

  const handleCloseOrder = () => {
    setDetailsOpen(false);
    setSelectedOrder(null);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('orderHistory')}
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder={t('searchOrderHistoryPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('orderId')}</TableCell>
              <TableCell>{t('date')}</TableCell>
              <TableCell>{t('customer')}</TableCell>
              <TableCell>{t('type')}</TableCell>
              <TableCell>{t('subTotal')}</TableCell>
              <TableCell>{t('deliveryFee')}</TableCell>
              <TableCell>{t('distance')}</TableCell>
              <TableCell>{t('totalAmount')}</TableCell>
              <TableCell>{t('Status')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  {t('noOrdersFound')}
                </TableCell>
              </TableRow>
            ) : (
              filteredOrders.map((order) => (
                <TableRow key={order.orderId || order.OrderId} onClick={() => handleOpenOrder(order)} style={{ cursor: 'pointer' }}>
                  <TableCell>#{order.orderNumber || order.OrderNumber || order.orderId || order.OrderId}</TableCell>
                  <TableCell>
                    {(() => {
                        const rawDate = order.orderDate || order.OrderDate || order.createdDate || order.CreatedDate;
                        if (!rawDate) return '-';
                        const d = new Date(rawDate);
                        if (isNaN(d.getTime())) return 'Invalid Date';
                        // Apply 11h offset
                        const adjusted = new Date(d.getTime() + (11 * 60 * 60 * 1000));
                        return adjusted.toLocaleString();
                    })()}
                  </TableCell>
                  <TableCell>{order.customerName || order.CustomerName || 'Guest'}</TableCell>
                  <TableCell>
                    {(order.IsPreOrderDineIn || order.isPreOrderDineIn)
                      ? t('dineIn')
                      : t('delivery')}
                  </TableCell>
                  <TableCell>
                    {(order.IsPreOrderDineIn || order.isPreOrderDineIn) 
                      ? t('isPreOrderLabel') 
                      : `${order.subTotal || order.SubTotal || 0} ${t('Currency')}`}
                  </TableCell>
                  <TableCell>
                     {(order.IsPreOrderDineIn || order.isPreOrderDineIn) 
                      ? t('isPreOrderLabel') 
                      : `${order.deliveryFee || order.DeliveryFee || 0} ${t('Currency')}`}
                  </TableCell>
                  <TableCell>
                     {(order.IsPreOrderDineIn || order.isPreOrderDineIn) 
                      ? t('isPreOrderLabel') 
                      : `${(order.distance || order.Distance || 0).toFixed(2)} km`}
                  </TableCell>
                  <TableCell>{order.totalAmount || order.TotalAmount} {t('Currency')}</TableCell>
                  <TableCell>
                    <Chip 
                      label={(() => {
                        const status = (order.orderStatus || order.OrderStatus || order.status || order.Status || '').toLowerCase();
                        return t(status);
                      })()} 
                      color={getStatusColor(order.orderStatus || order.OrderStatus || order.status || order.Status)} 
                      size="small" 
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={detailsOpen} onClose={handleCloseOrder} maxWidth="md" fullWidth>
        <DialogTitle>{t('OrderDetails')} #{selectedOrder?.orderId || selectedOrder?.OrderId}</DialogTitle>
        <DialogContent dividers>
          {selectedOrder && (
             <Grid container spacing={2}>
               <Grid item xs={12} md={6}>
                 <Typography variant="h6" gutterBottom>{t('customerDetails')}</Typography>
                 <Typography><strong>{t('customer')}:</strong> {selectedOrder.customerName || selectedOrder.CustomerName || 'Guest'}</Typography>
                 <Typography><strong>{t('type')}:</strong> {(selectedOrder.IsPreOrderDineIn || selectedOrder.isPreOrderDineIn) ? t('dineIn') : t('delivery')}</Typography>
               </Grid>
               
               <Grid item xs={12} md={6}>
                 <Typography variant="h6" gutterBottom>{t('paymentSummary')}</Typography>
                 <List dense>
                    {!(selectedOrder.IsPreOrderDineIn || selectedOrder.isPreOrderDineIn) && (
                        <>
                           <ListItem>
                             <ListItemText primary={t('subTotal')} />
                             <Typography>{selectedOrder.subTotal || selectedOrder.SubTotal || '0'} {t('Currency')}</Typography>
                           </ListItem>
                           <ListItem>
                             <ListItemText primary={t('deliveryFee')} />
                             <Typography>{selectedOrder.deliveryFee || selectedOrder.DeliveryFee || '0'} {t('Currency')}</Typography>
                           </ListItem>
                           <ListItem>
                             <ListItemText primary={t('distance')} />
                             <Typography>{(selectedOrder.distance || selectedOrder.Distance || 0).toFixed(2)} km</Typography>
                           </ListItem>
                        </>
                    )}
                    <ListItem>
                      <ListItemText primary={t('totalAmount')} primaryTypographyProps={{ fontWeight: 'bold' }} />
                      <Typography fontWeight="bold">{selectedOrder.totalAmount || selectedOrder.TotalAmount} {t('Currency')}</Typography>
                    </ListItem>
                 </List>
               </Grid>

               <Grid item xs={12}>
                 <Divider sx={{ my: 2 }} />
                 <Typography variant="h6" gutterBottom>{t('OrderItems')}</Typography>
                 {(() => {
                    let items = [];
                    try {
                        const raw = selectedOrder.OrderItemsJson || selectedOrder.orderItemsJson;
                        if (raw) {
                            items = typeof raw === 'string' ? JSON.parse(raw) : raw;
                        } else if (selectedOrder.orderItems || selectedOrder.OrderItems) {
                            items = selectedOrder.orderItems || selectedOrder.OrderItems;
                        }
                    } catch (e) {
                        items = selectedOrder.orderItems || selectedOrder.OrderItems || [];
                    }
                    if (!Array.isArray(items) && items) items = [items];
                    if (!items) items = [];

                    return (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{t('Products')}</TableCell>
                                    <TableCell>{t('Qty')}</TableCell>
                                    <TableCell>{t('Price')}</TableCell>
                                    <TableCell>{t('Total')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((item, idx) => {
                                    const pName = item.ProductName || item.productName || item.Name || item.name;
                                    const qty = item.Quantity || item.quantity;
                                    const price = item.Price || item.price;
                                    return (
                                        <TableRow key={idx}>
                                            <TableCell>{pName}</TableCell>
                                            <TableCell>{qty}</TableCell>
                                            <TableCell>{price}</TableCell>
                                            <TableCell>{(price * qty).toFixed(2)}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )
                 })()}
               </Grid>
             </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseOrder}>{t('close')}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
